// =============================================================================
// FE-18 — Delegation Empty State (shared)
// Reusable empty state component used across all delegation pages.
// Provides consistent visual treatment: icon, heading, description, CTA button.
// =============================================================================

import React from 'react';
import { CalendarClock, ClipboardList, UserCheck, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

// ---------------------------------------------------------------------------
// Icon set — map string key to component
// ---------------------------------------------------------------------------

const ICON_MAP = {
  calendar: CalendarClock,
  clipboard: ClipboardList,
  userCheck: UserCheck,
  search: Search,
} as const;

type IconKey = keyof typeof ICON_MAP;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DelegationEmptyStateProps {
  /** Which icon to display (default: calendar) */
  icon?: IconKey;
  /** Main heading */
  title: string;
  /** Supporting description text */
  description: string;
  /** Optional CTA label */
  ctaLabel?: string;
  /** Optional CTA handler */
  onCta?: () => void;
  /** Optional secondary action label */
  secondaryLabel?: string;
  /** Optional secondary action handler */
  onSecondary?: () => void;
  /** Additional className on the outer Card */
  className?: string;
  /** If true, renders without a Card wrapper (for inline use) */
  inline?: boolean;
}

// ---------------------------------------------------------------------------
// Inner content
// ---------------------------------------------------------------------------

interface EmptyStateContentProps extends Omit<DelegationEmptyStateProps, 'className' | 'inline'> {}

function EmptyStateContent({
  icon = 'calendar',
  title,
  description,
  ctaLabel,
  onCta,
  secondaryLabel,
  onSecondary,
}: EmptyStateContentProps) {
  const Icon = ICON_MAP[icon];

  return (
    <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
      {/* Icon circle */}
      <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
        <Icon className="h-7 w-7 text-primary" />
      </div>

      {/* Text */}
      <h3 className="text-base font-semibold mb-1.5">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">{description}</p>

      {/* Actions */}
      {(ctaLabel || secondaryLabel) && (
        <div className="flex items-center gap-2 mt-6">
          {ctaLabel && onCta && (
            <Button size="sm" onClick={onCta}>
              {ctaLabel}
            </Button>
          )}
          {secondaryLabel && onSecondary && (
            <Button size="sm" variant="outline" onClick={onSecondary}>
              {secondaryLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export const DelegationEmptyState: React.FC<DelegationEmptyStateProps> = ({
  className,
  inline = false,
  ...rest
}) => {
  if (inline) {
    return <EmptyStateContent {...rest} />;
  }

  return (
    <Card className={`border-dashed ${className ?? ''}`}>
      <CardContent className="p-0">
        <EmptyStateContent {...rest} />
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Pre-configured variants for common delegation empty states
// ---------------------------------------------------------------------------

/** Empty state for "My Delegations" when manager has no delegations yet */
interface MyDelegationsEmptyStateProps {
  onSetUp: () => void;
}

export const MyDelegationsEmptyState: React.FC<MyDelegationsEmptyStateProps> = ({ onSetUp }) => (
  <DelegationEmptyState
    icon="calendar"
    title="No delegations yet"
    description="Going out of office? Set up an approval delegate so timesheets keep moving while you're away."
    ctaLabel="Set Up Delegation"
    onCta={onSetUp}
  />
);

/** Empty state for audit page with no records at all */
export const AuditEmptyState: React.FC = () => (
  <DelegationEmptyState
    icon="clipboard"
    title="No delegations yet"
    description="Once managers create approval delegations they will appear here for review."
    inline
  />
);

/** Empty state for audit page when filters return no results */
export const AuditFilteredEmptyState: React.FC = () => (
  <DelegationEmptyState
    icon="search"
    title="No delegations match your filters"
    description="Try adjusting the status, date range, or name filters to find what you're looking for."
    inline
  />
);

/** Empty state for "Approvals on behalf of" when no delegated items exist */
export const DelegatedQueueEmptyState: React.FC = () => (
  <DelegationEmptyState
    icon="userCheck"
    title="No delegated approvals pending"
    description="There are currently no timesheets pending approval in your delegated queue."
    inline
    className="py-8"
  />
);
